import { useState } from "react";
import { AxiosError } from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { MapPin, ShieldCheck } from "lucide-react";

import { ApplyButton } from "@/features/applications";
import { useCurrentUser } from "@/features/auth";
import { FavouriteToggleButton } from "@/features/favourites";
import { RatingForm } from "@/features/ratings";
import { CheckInOutPanel } from "@/features/shift-events";
import { useShiftEvents } from "@/features/shift-events/model/useShiftEvents";
import { useCancelShift, useShift } from "@/features/shifts/model/useShifts";
import { CATEGORY_LABELS, ROLE_LABELS } from "@/features/shifts/types";
import { useManualCheckOut, WorkerTimesheetCard } from "@/features/timesheets";
import { formatTimeRange12h } from "@/shared/lib/time";
import { BackLink, Badge } from "@/shared/ui";

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function fmtRange(start: string, end: string | null): string {
  if (!end || end === start) return fmtDate(start);
  return `${fmtDate(start)} → ${fmtDate(end)}`;
}

export function ShiftDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const { data: shift, isLoading, error } = useShift(id);
  const cancel = useCancelShift();
  const manualCheckOut = useManualCheckOut();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showManualCheckoutConfirm, setShowManualCheckoutConfirm] = useState(false);
  const { data: events } = useShiftEvents(id);

  // Back goes to the worker's feed or the venue's shift management — whichever
  // role the user has — so the affordance always lands on a meaningful list.
  const backTo = user?.role === "VENUE" ? "/venue" : "/worker";
  const backLabel = user?.role === "VENUE" ? "Back to shifts" : "Back to feed";

  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackLink to={backTo} label={backLabel} />
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );
  }
  if (error || !shift) {
    return (
      <div className="space-y-4">
        <BackLink to={backTo} label={backLabel} />
        <div className="text-sm text-destructive">Shift not found.</div>
      </div>
    );
  }

  const isVenue = user?.role === "VENUE";
  const isWorker = user?.role === "WORKER";
  const isEditable = shift.status === "OPEN";
  const isCompleted = shift.status === "COMPLETED";
  const hasCheckIn = events?.some((e) => e.event_type === "CHECK_IN") ?? false;
  const hasCheckOut = events?.some((e) => e.event_type === "CHECK_OUT") ?? false;
  const canManualCheckout =
    isVenue && shift.status === "FILLED" && hasCheckIn && !hasCheckOut;
  const manualCheckOutErr = manualCheckOut.error
    ? manualCheckOut.error instanceof AxiosError
      ? (manualCheckOut.error.response?.data?.detail ?? "Couldn't check out worker")
      : manualCheckOut.error.message
    : null;

  return (
    <div className="space-y-6">
      <BackLink to={backTo} label={backLabel} />
      <div className="rounded-lg border bg-card p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold">
              {shift.event_name ?? ROLE_LABELS[shift.role]}
            </h1>
            <p className="mt-1 text-base font-medium">
              {shift.venue_name}
              {shift.venue_rating_avg != null && (
                <span className="ml-2 text-sm text-muted-foreground">
                  ★ {shift.venue_rating_avg.toFixed(1)}
                </span>
              )}
            </p>
            {shift.event_name && (
              <p className="text-sm text-muted-foreground">
                {ROLE_LABELS[shift.role]}
              </p>
            )}
            <p className="mt-1 flex items-start gap-1.5 text-sm text-copy-muted">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-copy-subtle" />
              <span className="break-words">{shift.venue_address}</span>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shift.venue_address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-primary hover:underline"
              >
                Directions
              </a>
            </p>
            <p className="text-sm text-muted-foreground">{fmtRange(shift.date, shift.end_date)}</p>
            {shift.category && (
              <p className="mt-1 text-xs text-muted-foreground">
                Category: {CATEGORY_LABELS[shift.category]}
              </p>
            )}
            {shift.required_certifications && shift.required_certifications.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 text-xs text-copy-muted">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Required certifications:
                </span>
                {shift.required_certifications.map((cert) => (
                  <Badge key={cert} variant="neutral">
                    {cert}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <span className="rounded-full bg-secondary px-2 py-1 text-xs font-medium">{shift.status}</span>
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">Time</dt>
            <dd className="font-medium">
              {formatTimeRange12h(shift.start_time, shift.end_time)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Pay</dt>
            <dd className="font-medium">${shift.pay_rate} / hour</dd>
          </div>
          {shift.dress_code && (
            <div className="md:col-span-2">
              <dt className="text-muted-foreground">Dress code</dt>
              <dd>{shift.dress_code}</dd>
            </div>
          )}
          {shift.notes && (
            <div className="md:col-span-2">
              <dt className="text-muted-foreground">Notes</dt>
              <dd>{shift.notes}</dd>
            </div>
          )}
          {shift.transportation_offered && (
            <div className="md:col-span-2">
              <dt className="text-muted-foreground">Transportation</dt>
              <dd>
                Offered by venue
                {shift.transportation_notes && (
                  <span className="block text-xs text-muted-foreground">
                    {shift.transportation_notes}
                  </span>
                )}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {isVenue && (
        <div className="flex flex-wrap items-center gap-3">
          {isEditable && (
            <button
              onClick={() => navigate(`/venue/shifts/${shift.id}/edit`)}
              className="rounded-md border border-input px-4 py-2 text-sm hover:bg-surface-sunken"
            >
              Edit
            </button>
          )}
          <button
            onClick={() => navigate(`/venue/shifts/${shift.id}/applicants`)}
            className="rounded-md border border-input px-4 py-2 text-sm hover:bg-surface-sunken"
          >
            View applicants
          </button>
          {(shift.status === "OPEN" || shift.status === "FILLED") && (
            <button
              onClick={() => setShowCancelConfirm(true)}
              className="rounded-md border border-destructive px-4 py-2 text-sm text-destructive hover:bg-destructive-hover hover:text-primary-foreground hover:border-destructive-hover"
            >
              Cancel shift
            </button>
          )}
          {canManualCheckout && (
            <button
              onClick={() => setShowManualCheckoutConfirm(true)}
              title="Use this if the worker's phone is dead. Closes the shift on their behalf."
              className="rounded-md border border-input px-4 py-2 text-sm hover:bg-surface-sunken"
            >
              Check out worker
            </button>
          )}
        </div>
      )}
      {manualCheckOutErr && isVenue && (
        <p className="text-sm text-destructive">{manualCheckOutErr}</p>
      )}

      {isWorker && <ApplyButton shiftId={shift.id} shiftStatus={shift.status} />}

      {isWorker && (shift.status === "FILLED" || shift.status === "COMPLETED") && (
        <CheckInOutPanel
          shiftId={shift.id}
          shiftStatus={shift.status}
          venueAddress={shift.venue_address}
        />
      )}

      {isWorker && isCompleted && <WorkerTimesheetCard shiftId={shift.id} />}

      {isCompleted && (
        <RatingForm shiftId={shift.id} direction={isVenue ? "VENUE_TO_WORKER" : "WORKER_TO_VENUE"} />
      )}

      {isVenue && isCompleted && shift.confirmed_worker_id && (
        <div className="rounded-lg border bg-card p-4">
          <p className="mb-2 text-sm font-medium">Worked well together?</p>
          <FavouriteToggleButton workerId={shift.confirmed_worker_id} />
        </div>
      )}

      {showManualCheckoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg border bg-card p-6 shadow-lg">
            <h2 className="text-lg font-semibold">Check out worker on their behalf?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Use this only when the worker can't check out themselves (e.g., dead phone). The
              shift's scheduled end time will be used. The timesheet will be flagged "closed by venue".
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowManualCheckoutConfirm(false)}
                disabled={manualCheckOut.isPending}
                className="rounded-md border border-input px-3 py-2 text-sm hover:bg-surface-sunken"
              >
                Never mind
              </button>
              <button
                onClick={() =>
                  manualCheckOut.mutate(shift.id, {
                    onSuccess: () => setShowManualCheckoutConfirm(false),
                  })
                }
                disabled={manualCheckOut.isPending}
                className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {manualCheckOut.isPending ? "Closing…" : "Close shift"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg border bg-card p-6 shadow-lg">
            <h2 className="text-lg font-semibold">Cancel this shift?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {shift.status === "FILLED"
                ? "A worker has already been confirmed for this shift. They'll be notified."
                : "This will mark the shift as cancelled. You can't undo this."}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancel.isPending}
                className="rounded-md border border-input px-3 py-2 text-sm hover:bg-surface-sunken"
              >
                Keep shift
              </button>
              <button
                onClick={() =>
                  cancel.mutate(shift.id, {
                    onSuccess: () => {
                      setShowCancelConfirm(false);
                      navigate("/venue");
                    },
                  })
                }
                disabled={cancel.isPending}
                className="rounded-md bg-destructive px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-destructive-hover disabled:opacity-50"
              >
                {cancel.isPending ? "Cancelling..." : "Cancel shift"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
